import React, { useEffect, useMemo, useState } from 'react';
import { LeaveTypeConfigurationDto } from '../../types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  EventAvailable as LeaveIcon,
  FileDownload as ExportIcon,
  Group as TeamIcon,
  Send as ApplyIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { calendarApi } from '../../api/calendarApi';
import { leaveApi } from '../../api/leaveApi';
import { employeeApi } from '../../api/employeeApi';
import { shiftApi } from '../../api/shiftApi';
import { generalDefaultSettingApi } from '../../api/generalDefaultSettingApi';
import {
  CreateLeaveRequest,
  HolidayDto,
  LeaveBalanceDto,
  LeavePeriodDto,
  LeaveRequestDto,
  LeaveRequestStatusColors,
  LeaveRequestStatusLabels,
  LeaveTypeDto,
  WhoIsOnLeaveDto,
} from '../../types';
import { RootState as StoreRootState } from '../../store/store';
import { toast } from 'react-toastify';

const toDateInputValue = (value: Date) => value.toISOString().slice(0, 10);
const parseDateInputValue = (value: string) => new Date(`${value}T00:00:00`);

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

const isEarlierDate = (value: string, compareWith: string) => Boolean(value && compareWith && value < compareWith);

const getWeekOfMonth = (date: Date): number => {
  const day = date.getDate();
  return Math.floor((day - 1) / 7) + 1;
};

const isDateMatchedByWeekOffRule = (
  date: Date,
  weekOffRules: Array<{ dayOfWeek: number; weekOfMonth: number }>
) => {
  const backendDayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
  const weekOfMonth = getWeekOfMonth(date);
  return weekOffRules.some((rule) => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
};

type WorkWeekDayConfig = {
  isWorkingDay: boolean;
  isHalfDay: boolean;
};

type ApplyLeaveFormErrors = {
  leaveTypeId?: string;
  leavePeriodId?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  form?: string;
};

const defaultWorkWeek: Record<string, WorkWeekDayConfig> = {
  Monday: { isWorkingDay: true, isHalfDay: false },
  Tuesday: { isWorkingDay: true, isHalfDay: false },
  Wednesday: { isWorkingDay: true, isHalfDay: false },
  Thursday: { isWorkingDay: true, isHalfDay: false },
  Friday: { isWorkingDay: true, isHalfDay: false },
  Saturday: { isWorkingDay: false, isHalfDay: false },
  Sunday: { isWorkingDay: false, isHalfDay: false },
};

const formatDateKey = (value: Date | string) => {
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calculateNumberOfDays = (
  startDate: string,
  endDate: string,
  isHalfDay: boolean,
  leaveType?: LeaveTypeDto,
  holidayDateKeys?: Set<string>,
  workWeekByDay?: Record<string, WorkWeekDayConfig>
): number => {
  if (!startDate || !endDate) {
    return isHalfDay ? 0.5 : 1;
  }

  if (isHalfDay) {
    return 0.5;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < start) {
    return 1;
  }

  const effectiveLeaveType = leaveType ?? ({ includeHolidays: true, includeWeekends: true } as LeaveTypeDto);
  const effectiveWorkWeek = workWeekByDay ?? defaultWorkWeek;

  let totalDays = 0;
  const current = new Date(start);
  while (current <= end) {
    const dateKey = formatDateKey(current);
    const dayName = current.toLocaleDateString('en-US', { weekday: 'long' });
    const dayConfig = effectiveWorkWeek[dayName] ?? defaultWorkWeek[dayName] ?? { isWorkingDay: true, isHalfDay: false };

    if (!effectiveLeaveType.includeHolidays && holidayDateKeys?.has(dateKey)) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    if (!effectiveLeaveType.includeWeekends && !dayConfig.isWorkingDay) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    totalDays += dayConfig.isHalfDay ? 0.5 : 1;
    current.setDate(current.getDate() + 1);
  }

  return Math.max(totalDays, 0);
};

const calculateNumberOfDaysFromCalendar = (
  startDate: string,
  endDate: string,
  isHalfDay: boolean,
  leaveType: LeaveTypeDto | undefined,
  holidayDateKeys: Set<string>,
  workWeekByDay: Record<string, WorkWeekDayConfig>,
  calendarStatuses: Array<{ date: string; status: string }>,
  weekOffRules: Array<{ dayOfWeek: number; weekOfMonth: number }>
): number => {
  if (!startDate || !endDate) {
    return isHalfDay ? 0.5 : 1;
  }

  if (isHalfDay) {
    return 0.5;
  }

  const effectiveLeaveType = leaveType ?? ({ includeHolidays: true, includeWeekends: true } as LeaveTypeDto);
  const statusByDate = new Map(
    calendarStatuses.map((item) => [formatDateKey(item.date), (item.status || '').trim().toLowerCase()])
  );

  let totalDays = 0;
  const current = parseDateInputValue(startDate);
  const end = parseDateInputValue(endDate);

  while (current <= end) {
    const dateKey = formatDateKey(current);
    const dayName = current.toLocaleDateString('en-US', { weekday: 'long' });
    const dayConfig = workWeekByDay[dayName] ?? defaultWorkWeek[dayName] ?? { isWorkingDay: true, isHalfDay: false };
    const calendarStatus = statusByDate.get(dateKey);
    const isHoliday = calendarStatus === 'holiday' || holidayDateKeys.has(dateKey);
    const isWeekOff =
      calendarStatus === 'weekoff' ||
      isDateMatchedByWeekOffRule(current, weekOffRules) ||
      !dayConfig.isWorkingDay;

    if (!effectiveLeaveType.includeHolidays && isHoliday) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    if (!effectiveLeaveType.includeWeekends && isWeekOff) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    totalDays += dayConfig.isHalfDay ? 0.5 : 1;
    current.setDate(current.getDate() + 1);
  }

  return Math.max(totalDays, 0);
};

const EmployeeLeaveModule: React.FC = () => {
  const { user } = useSelector((state: StoreRootState) => state.auth);
  const canApprove = user?.permissions?.includes('ApproveLeave') ?? false;
  const [currentEmployeeId, setCurrentEmployeeId] = useState<string>('');
  const [employeeProfileMissing, setEmployeeProfileMissing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [leavePeriods, setLeavePeriods] = useState<LeavePeriodDto[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequestDto[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalanceDto[]>([]);
  const [onLeave, setOnLeave] = useState<WhoIsOnLeaveDto[]>([]);
  const [teamOnLeave, setTeamOnLeave] = useState<WhoIsOnLeaveDto[]>([]);
  const [holidayDateKeys, setHolidayDateKeys] = useState<Set<string>>(new Set());
  const [workWeekByDay, setWorkWeekByDay] = useState<Record<string, WorkWeekDayConfig>>(defaultWorkWeek);
  const [applyOpen, setApplyOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequestDto | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
  });
  const [form, setForm] = useState<CreateLeaveRequest>({
    leaveTypeId: '',
    leavePeriodId: '',
    startDate: '',
    endDate: '',
    numberOfDays: 1,
    isHalfDay: false,
    halfDayType: '',
    reason: '',
    attachment: null,
  });
  const [applyErrors, setApplyErrors] = useState<ApplyLeaveFormErrors>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const activeLeaveTypes = useMemo(() => {
    const activeItems = leaveTypes.filter((item) => item.isActive);
    return activeItems.length > 0 ? activeItems : leaveTypes;
  }, [leaveTypes]);

  const activeLeavePeriods = useMemo(() => {
    const activeItems = leavePeriods.filter((item) => item.isActive);
    return activeItems.length > 0 ? activeItems : leavePeriods;
  }, [leavePeriods]);

  const selectedLeaveType = useMemo(
    () => leaveTypes.find((item) => item.id === form.leaveTypeId),
    [leaveTypes, form.leaveTypeId]
  );

  const refreshData = async () => {
    setLoading(true);
    try {
      let resolvedEmployeeId = '';
      let missingProfile = false;

      try {
        const currentEmployee = await employeeApi.getCurrentEmployee();
        resolvedEmployeeId = currentEmployee.id;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          missingProfile = true;
        } else {
          throw error;
        }
      }

      setCurrentEmployeeId(resolvedEmployeeId);
      setEmployeeProfileMissing(missingProfile);

      const [types, periods, requests, balanceData, orgOnLeave, teamLeaves] = await Promise.all([
        leaveApi.getLeaveTypes(),
        leaveApi.getLeavePeriods(),
        resolvedEmployeeId
          ? leaveApi.getLeaveRequests({
            employeeId: resolvedEmployeeId,
            status: filters.status ? Number(filters.status) : undefined,
            leaveTypeId: filters.leaveTypeId || undefined,
            fromDate: filters.fromDate || undefined,
            toDate: filters.toDate || undefined,
          })
          : Promise.resolve([]),
        resolvedEmployeeId
          ? leaveApi.getLeaveBalance({ employeeId: resolvedEmployeeId })
          : Promise.resolve([]),
        leaveApi.getWhoIsOnLeave({ date: toDateInputValue(new Date()) }),
        canApprove ? leaveApi.getWhoIsOnLeave({ date: toDateInputValue(new Date()), teamOnly: true }) : Promise.resolve([]),
      ]);

      setLeaveTypes(types);
      setLeavePeriods(periods);
      setLeaveRequests(requests);
      setLeaveBalance(balanceData);
      setOnLeave(orgOnLeave);
      setTeamOnLeave(teamLeaves);

      const [defaultSettings, workWeeks, holidayGroups] = await Promise.all([
        generalDefaultSettingApi.get(),
        shiftApi.getWorkWeekConfiguration(),
        Promise.all(
          Array.from(new Set(periods.flatMap((item) => [new Date(item.startDate).getFullYear(), new Date(item.endDate).getFullYear()])))
            .map((year) => leaveApi.getHolidays({ year }))
        ),
      ]);

      setHolidayDateKeys(new Set(
        holidayGroups.flat().filter((holiday: HolidayDto) => holiday.isActive).map((holiday: HolidayDto) => formatDateKey(holiday.date))
      ));

      const configuredWorkWeek = (workWeeks || []).find((item: any) => item.id === defaultSettings.setting?.workWeekId);
      if (configuredWorkWeek?.days?.length) {
        setWorkWeekByDay(configuredWorkWeek.days.reduce((acc: Record<string, WorkWeekDayConfig>, item: any) => {
          const dayName = item.dayName || item.day;
          if (dayName) {
            acc[dayName] = {
              isWorkingDay: item.isWorkingDay ?? item.workingDay ?? false,
              isHalfDay: item.isHalfDay ?? item.halfDay ?? false,
            };
          }
          return acc;
        }, { ...defaultWorkWeek }));
      } else {
        setWorkWeekByDay(defaultWorkWeek);
      }

      const selectablePeriods = periods.filter((item) => item.isActive);
      const periodOptions = selectablePeriods.length > 0 ? selectablePeriods : periods;
      const selectableTypes = types.filter((item) => item.isActive);
      const typeOptions = selectableTypes.length > 0 ? selectableTypes : types;

      setForm((prev) => ({
        ...prev,
        leavePeriodId: prev.leavePeriodId || periodOptions[0]?.id || '',
        leaveTypeId: prev.leaveTypeId || typeOptions[0]?.id || '',
      }));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.leaveTypeId, filters.fromDate, filters.toDate, canApprove]);

  useEffect(() => {
    if (!applyOpen) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      leaveTypeId: prev.leaveTypeId || activeLeaveTypes[0]?.id || '',
      leavePeriodId: prev.leavePeriodId || activeLeavePeriods[0]?.id || '',
    }));
  }, [applyOpen, activeLeavePeriods, activeLeaveTypes]);

  useEffect(() => {
    if (!form.startDate || !form.endDate) {
      return;
    }

    const nextNumberOfDays = calculateNumberOfDays(
      form.startDate,
      form.endDate,
      form.isHalfDay,
      selectedLeaveType,
      holidayDateKeys,
      workWeekByDay
    );

    setForm((prev) => (prev.numberOfDays === nextNumberOfDays ? prev : { ...prev, numberOfDays: nextNumberOfDays }));
  }, [form.startDate, form.endDate, form.isHalfDay, selectedLeaveType, holidayDateKeys, workWeekByDay]);

  const [selectedLeaveTypeConfig, setSelectedLeaveTypeConfig] = useState<LeaveTypeConfigurationDto | null>(null);
  // Fetch leave type config when leave type changes
  useEffect(() => {
    const fetchConfig = async () => {
      if (form.leaveTypeId) {
        try {
          const config = await leaveApi.getLeaveTypeConfiguration(form.leaveTypeId);
          setSelectedLeaveTypeConfig(config);
        } catch {
          setSelectedLeaveTypeConfig(null);
        }
      } else {
        setSelectedLeaveTypeConfig(null);
      }
    };
    fetchConfig();
  }, [form.leaveTypeId]);

  useEffect(() => {
    if (!form.startDate || !form.endDate) {
      return;
    }

    let isActive = true;

    const syncNumberOfDaysWithCalendar = async () => {
      const fallbackDays = calculateNumberOfDays(
        form.startDate,
        form.endDate,
        form.isHalfDay,
        selectedLeaveType,
        holidayDateKeys,
        workWeekByDay
      );

      if (!currentEmployeeId || form.isHalfDay) {
        if (isActive) {
          setForm((prev) => (prev.numberOfDays === fallbackDays ? prev : { ...prev, numberOfDays: fallbackDays }));
        }
        return;
      }

      try {
        const [calendarStatuses, weekOffRules] = await Promise.all([
          calendarApi.getEmployeeCalendarStatus(currentEmployeeId, form.startDate, form.endDate),
          calendarApi.getEmployeeWeekOffRules(currentEmployeeId),
        ]);

        const nextNumberOfDays = calculateNumberOfDaysFromCalendar(
          form.startDate,
          form.endDate,
          form.isHalfDay,
          selectedLeaveType,
          holidayDateKeys,
          workWeekByDay,
          calendarStatuses,
          weekOffRules
        );

        if (isActive) {
          setForm((prev) => (prev.numberOfDays === nextNumberOfDays ? prev : { ...prev, numberOfDays: nextNumberOfDays }));
        }
      } catch {
        if (isActive) {
          setForm((prev) => (prev.numberOfDays === fallbackDays ? prev : { ...prev, numberOfDays: fallbackDays }));
        }
      }
    };

    syncNumberOfDaysWithCalendar();

    return () => {
      isActive = false;
    };
  }, [currentEmployeeId, form.startDate, form.endDate, form.isHalfDay, selectedLeaveType, holidayDateKeys, workWeekByDay]);

  const clearApplyError = (field: keyof ApplyLeaveFormErrors) => {
    setApplyErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
  };

  const handleApplyLeave = async () => {
    if (!currentEmployeeId) {
      setApplyErrors({ form: 'No employee profile is linked to this login.' });
      return;
    }

    const nextErrors: ApplyLeaveFormErrors = {};
    if (!form.leaveTypeId) nextErrors.leaveTypeId = 'Leave type is required.';
    if (!form.leavePeriodId) nextErrors.leavePeriodId = 'Leave period is required.';
    if (!form.startDate) nextErrors.startDate = 'From date is required.';
    if (!form.endDate) nextErrors.endDate = 'To date is required.';
    if (!form.reason.trim()) nextErrors.reason = 'Reason is required.';

    if (Object.keys(nextErrors).length > 0) {
      setApplyErrors(nextErrors);
      return;
    }

    if (!form.leaveTypeId || !form.leavePeriodId || !form.startDate || !form.endDate || !form.reason.trim()) {
      return;
    }

    // Leave balance validation
    const selectedLeaveBalance = leaveBalance.find(
      (b) => b.leaveTypeId === form.leaveTypeId && b.leavePeriodId === form.leavePeriodId
    );
    if (
      selectedLeaveBalance &&
      selectedLeaveTypeConfig &&
      !selectedLeaveTypeConfig.allowNegativeBalance &&
      form.numberOfDays > selectedLeaveBalance.remainingDays
    ) {
      setApplyErrors({ form: `Cannot apply for more than available balance (${selectedLeaveBalance.remainingDays} days).` });
      return;
    }

    if (form.startDate === form.endDate) {
      try {
        const [calendarStatus, weekOffRules] = await Promise.all([
          calendarApi.getEmployeeCalendarStatus(currentEmployeeId, form.startDate, form.endDate),
          calendarApi.getEmployeeWeekOffRules(currentEmployeeId),
        ]);

        const selectedDayStatus = (calendarStatus[0]?.status || '').trim().toLowerCase();
        if (selectedDayStatus === 'holiday') {
          setApplyErrors({ form: 'Selected date is a holiday.' });
          return;
        }

        const selectedDateObj = new Date(`${form.startDate}T00:00:00`);
        const backendDayOfWeek = selectedDateObj.getDay() === 0 ? 7 : selectedDateObj.getDay();
        const weekOfMonth = getWeekOfMonth(selectedDateObj);
        const isWeekOffByRule = weekOffRules.some(
          (rule) => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth
        );

        if (selectedDayStatus === 'weekoff' || isWeekOffByRule) {
          setApplyErrors({ form: 'Selected date is a WeekOff.' });
          return;
        }
      } catch {
        setApplyErrors({ form: 'Unable to validate holiday/week off for selected date.' });
        return;
      }
    }

    setSubmitting(true);
    try {
      await leaveApi.createLeaveRequest({
        ...form,
        employeeId: currentEmployeeId,
      });
      setApplyOpen(false);
      setForm({
        leaveTypeId: '',
        leavePeriodId: activeLeavePeriods[0]?.id || '',
        startDate: '',
        endDate: '',
        numberOfDays: 1,
        isHalfDay: false,
        halfDayType: '',
        reason: '',
        attachment: null,
      });
      setApplyErrors({});
      toast.success('Leave applied successfully');
      await refreshData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to apply leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await leaveApi.exportLeaveRequests({
        status: filters.status ? Number(filters.status) : undefined,
        leaveTypeId: filters.leaveTypeId || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      });
      downloadBlob(blob, `my-leave-${toDateInputValue(new Date())}.xlsx`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export leave requests');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadAttachment = async (request: LeaveRequestDto) => {
    try {
      const blob = await leaveApi.downloadLeaveAttachment(request.id);
      downloadBlob(blob, request.attachmentFileName || `leave-attachment-${request.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download attachment');
    }
  };

  const renderStatusChip = (status: number) => (
    <Chip
      size="small"
      color={LeaveRequestStatusColors[status]}
      label={LeaveRequestStatusLabels[status]}
    />
  );

  const renderProgressTab = (request: LeaveRequestDto) => (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">Applied</Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(request.createdAt).toLocaleString()}
        </Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">Current Status</Typography>
        <Box sx={{ mt: 1 }}>{renderStatusChip(request.status)}</Box>
        {request.adminRemarks && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Remarks: {request.adminRemarks}
          </Typography>
        )}
        {request.approvedAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Actioned on {new Date(request.approvedAt).toLocaleString()}
            {request.approvedByName ? ` by ${request.approvedByName}` : ''}
          </Typography>
        )}
      </Paper>
    </Stack>
  );

  const handleFilterFromDateChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      fromDate: value,
      toDate: isEarlierDate(prev.toDate, value) ? value : prev.toDate,
    }));
  };

  const handleFilterToDateChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      toDate: isEarlierDate(value, prev.fromDate) ? prev.fromDate : value,
    }));
  };

  const handleFormStartDateChange = (value: string) => {
    setForm((prev) => {
      const nextEndDate = prev.isHalfDay || isEarlierDate(prev.endDate, value) ? value : prev.endDate;
      return {
        ...prev,
        startDate: value,
        endDate: nextEndDate,
        numberOfDays: calculateNumberOfDays(value, nextEndDate, prev.isHalfDay, selectedLeaveType, holidayDateKeys, workWeekByDay),
      };
    });
  };

  const handleFormEndDateChange = (value: string) => {
    setForm((prev) => {
      const nextEndDate = isEarlierDate(value, prev.startDate) ? prev.startDate : value;
      return {
        ...prev,
        endDate: nextEndDate,
        numberOfDays: calculateNumberOfDays(prev.startDate, nextEndDate, prev.isHalfDay, selectedLeaveType, holidayDateKeys, workWeekByDay),
      };
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #f5f7ff 0%, #eef6f0 100%)',
          border: '1px solid #dce5f0',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>Leave Management</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Apply for leave, track approvals, monitor balance, and check who is away today.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport} disabled={exporting} sx={{textTransform: 'none'}}>
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              variant="contained"
              startIcon={<ApplyIcon />}
              onClick={() => {
                setApplyErrors({});
                setApplyOpen(true);
              }}
              sx={{textTransform: 'none'}}
            >
              Apply Leave
            </Button>
          </Stack>
        </Box>
      </Paper>

      {employeeProfileMissing && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your login is not linked to an employee profile yet, so personal leave balance and leave application are unavailable.
          Please ask HR or your administrator to verify your Login Details, Official Email, and account mapping in your employee profile.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {leaveBalance.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography color="text.secondary">No leave balances found for the active period.</Typography>
            </Paper>
          </Grid>
        ) : (
          leaveBalance.map((item) => (
  <Grid key={`${item.leaveTypeId}-${item.leavePeriodId}`} size={{ xs: 12, sm: 6, lg: 3 }}>
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 2,
        transition: '0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={700}>
          {item.leaveTypeName}
        </Typography>

        <LeaveIcon
          sx={{
            color: item.leaveTypeColor || '#3B82F6',
            bgcolor: '#EFF6FF',
            borderRadius: '50%',
            p: 0.7
          }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {item.leavePeriodName}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {/* Assigned */}
        <Chip
          label={`Assigned ${item.totalDays}`}
          size="small"
          sx={{
            bgcolor: '#DBEAFE',
            color: '#1D4ED8',
            fontWeight: 600
          }}
        />

        {/* Carry Forward */}
        <Chip
          label={`Carry ${item.carryForwardDays}`}
          size="small"
          sx={{
            bgcolor: item.carryForwardDays > 0 ? '#EDE9FE' : '#F3F4F6',
            color: item.carryForwardDays > 0 ? '#6D28D9' : '#6B7280',
            fontWeight: 600
          }}
        />

        {/* Total */}
        <Chip
          label={`Total ${item.totalDays + item.carryForwardDays}`}
          size="small"
          sx={{
            bgcolor: '#E0E7FF',
            color: '#3730A3',
            fontWeight: 600
          }}
        />

        {/* Used */}
        <Chip
          label={`Used ${item.usedDays}`}
          size="small"
          sx={{
            bgcolor: '#FEF3C7',
            color: '#92400E',
            fontWeight: 600
          }}
        />

        {/* Remaining */}
        <Chip
          label={`Left ${item.remainingDays}`}
          size="small"
          sx={{
            bgcolor: item.remainingDays <= 2 ? '#FEE2E2' : '#D1FAE5',
            color: item.remainingDays <= 2 ? '#991B1B' : '#065F46',
            fontWeight: 600
          }}
        />
      </Stack>
    </Paper>
  </Grid>
))
        )}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">Approved</MenuItem>
                <MenuItem value="2">Rejected</MenuItem>
                <MenuItem value="3">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Leave Type</InputLabel>
              <Select
                value={filters.leaveTypeId}
                label="Leave Type"
                onChange={(event) => setFilters((prev) => ({ ...prev, leaveTypeId: event.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {activeLeaveTypes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              type="date"
              label="From Date"
              size="small"
              fullWidth
              value={filters.fromDate}
              onChange={(event) => handleFilterFromDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              type="date"
              label="To Date"
              size="small"
              fullWidth
              value={filters.toDate}
              onChange={(event) => handleFilterToDateChange(event.target.value)}
              inputProps={{ min: filters.fromDate || undefined }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Total Records: {leaveRequests.length}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Attachment</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No leave requests found.</TableCell>
                  </TableRow>
                ) : (
                  leaveRequests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>{request.leaveTypeName}</TableCell>
                      <TableCell>{new Date(request.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(request.endDate).toLocaleDateString()}</TableCell>
                      <TableCell>{request.numberOfDays}</TableCell>
                      <TableCell>{renderStatusChip(request.status)}</TableCell>
                      <TableCell>{request.hasAttachment ? 'Yes' : '-'}</TableCell>
                      <TableCell>{request.reason || '-'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => { setSelectedRequest(request); setDetailsTab(0); }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6">Who Is On Leave?</Typography>
                <TeamIcon color="action" />
              </Box>
              <Stack spacing={1}>
                {(canApprove ? teamOnLeave : onLeave).slice(0, 6).map((item) => (
                  <Paper key={item.leaveRequestId} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>{item.employeeName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.leaveTypeName} • {new Date(item.startDate).toLocaleDateString()} - {new Date(item.endDate).toLocaleDateString()}
                    </Typography>
                  </Paper>
                ))}
                {(canApprove ? teamOnLeave : onLeave).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nobody is on leave today.
                  </Typography>
                )}
              </Stack>
            </Paper>
            {canApprove && (
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Team Leave Snapshot</Typography>
                <Typography variant="body2" color="text.secondary">
                  {teamOnLeave.length} team member(s) are currently on approved leave.
                </Typography>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={applyOpen}
        onClose={() => {
          if (!submitting) {
            setApplyErrors({});
            setApplyOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Apply leave</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {applyErrors.form && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{applyErrors.form}</Alert>
              </Grid>
            )}

            {/* Row 1: Leave type + Leave period */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" required error={Boolean(applyErrors.leaveTypeId)}>
                <InputLabel>Leave type</InputLabel>
                <Select
                  value={form.leaveTypeId}
                  label="Leave type"
                  onChange={(event) => {
                    clearApplyError('leaveTypeId');
                    setForm((prev) => {
                      const nextLeaveType = leaveTypes.find((item) => item.id === event.target.value);
                      const nextIsHalfDay = nextLeaveType?.allowHalfDay ? prev.isHalfDay : false;
                      return {
                        ...prev,
                        leaveTypeId: event.target.value,
                        isHalfDay: nextIsHalfDay,
                        halfDayType: nextIsHalfDay ? prev.halfDayType : '',
                        numberOfDays: calculateNumberOfDays(prev.startDate, prev.endDate, nextIsHalfDay, nextLeaveType, holidayDateKeys, workWeekByDay),
                      };
                    });
                  }}
                >
                  {activeLeaveTypes.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                  ))}
                </Select>
                {applyErrors.leaveTypeId && <FormHelperText>{applyErrors.leaveTypeId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" required error={Boolean(applyErrors.leavePeriodId)}>
                <InputLabel>Leave period</InputLabel>
                <Select
                  value={form.leavePeriodId}
                  label="Leave period"
                  onChange={(event) => {
                    clearApplyError('leavePeriodId');
                    setForm((prev) => ({ ...prev, leavePeriodId: event.target.value }));
                  }}
                >
                  {activeLeavePeriods.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                  ))}
                </Select>
                {applyErrors.leavePeriodId && <FormHelperText>{applyErrors.leavePeriodId}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Row 2: From date + To date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                type="date"
                label="From date"
                size="small"
                fullWidth
                required
                error={Boolean(applyErrors.startDate)}
                helperText={applyErrors.startDate}
                value={form.startDate}
                onChange={(event) => {
                  clearApplyError('startDate');
                  handleFormStartDateChange(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                type="date"
                label="To date"
                size="small"
                fullWidth
                required
                error={Boolean(applyErrors.endDate)}
                helperText={applyErrors.endDate}
                disabled={form.isHalfDay}
                value={form.endDate}
                onChange={(event) => {
                  clearApplyError('endDate');
                  handleFormEndDateChange(event.target.value);
                }}
                inputProps={{ min: form.startDate || undefined }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Row 3: Days + Balance */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  label="Days"
                  size="small"
                  sx={{ width: 100 }}
                  value={form.numberOfDays}
                  InputProps={{ readOnly: true }}
                />
                {form.leaveTypeId && form.leavePeriodId && (() => {
                  const selectedLeaveBalance = leaveBalance.find(
                    (b) => b.leaveTypeId === form.leaveTypeId && b.leavePeriodId === form.leavePeriodId
                  );
                  const days = selectedLeaveBalance?.remainingDays;
                  return days === undefined ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>Leave balance not available.</Alert>
                  ) : (
                    <Alert severity={days > 0 ? 'info' : 'warning'} sx={{ py: 0.5 }}>
                      Available balance: <b>{days}</b> day{days === 1 ? '' : 's'}
                    </Alert>
                  );
                })()}
              </Box>
            </Grid>

            {/* Row 4: Half day checkbox + Half day type (conditional) */}
            {selectedLeaveType?.allowHalfDay && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isHalfDay}
                        onChange={(event) =>
                          setForm((prev) => {
                            const nextIsHalfDay = event.target.checked;
                            const nextEndDate = nextIsHalfDay ? prev.startDate : prev.endDate;
                            return {
                              ...prev,
                              isHalfDay: nextIsHalfDay,
                              halfDayType: nextIsHalfDay ? prev.halfDayType || 'First Half' : '',
                              endDate: nextEndDate,
                              numberOfDays: calculateNumberOfDays(prev.startDate, nextEndDate, nextIsHalfDay, selectedLeaveType, holidayDateKeys, workWeekByDay),
                            };
                          })
                        }
                      />
                    }
                    label="Half day leave"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small" disabled={!form.isHalfDay}>
                    <InputLabel>Half day type</InputLabel>
                    <Select
                      value={form.halfDayType || ''}
                      label="Half day type"
                      onChange={(event) => setForm((prev) => ({ ...prev, halfDayType: event.target.value }))}
                    >
                      <MenuItem value="First Half">First half</MenuItem>
                      <MenuItem value="Second Half">Second half</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Row 5: Reason */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Reason"
                size="small"
                fullWidth
                required
                multiline
                rows={4}
                error={Boolean(applyErrors.reason)}
                helperText={applyErrors.reason}
                value={form.reason}
                onChange={(event) => {
                  clearApplyError('reason');
                  setForm((prev) => ({ ...prev, reason: event.target.value }));
                }}
              />
            </Grid>

            {/* Row 6: Attachment */}
            <Grid size={{ xs: 12 }}>
              <Button variant="outlined" component="label">
                {form.attachment ? `Attachment: ${form.attachment.name}` : 'Upload attachment'}
                <input
                  hidden
                  type="file"
                  onChange={(event) => setForm((prev) => ({ ...prev, attachment: event.target.files?.[0] || null }))}
                />
              </Button>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApplyErrors({});
              setApplyOpen(false);
            }}
            disabled={submitting}
            sx={{textTransform: 'none'}}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApplyLeave} disabled={submitting} sx={{textTransform: 'none'}}>
            {submitting ? 'Submitting...' : 'Submit leave'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!selectedRequest} onClose={() => setSelectedRequest(null)} maxWidth="md" fullWidth>
        <DialogTitle>Leave Details</DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box>
              <Tabs value={detailsTab} onChange={(_, value) => setDetailsTab(value)} sx={{ mb: 2 }}>
                <Tab label="Details" />
                <Tab label="Attachment" />
                <Tab label="Progress" />
              </Tabs>
              {detailsTab === 0 && (
                <Stack spacing={1.25}>
                  <Typography><strong>Leave Type:</strong> {selectedRequest.leaveTypeName}</Typography>
                  <Typography><strong>Leave Period:</strong> {selectedRequest.leavePeriodName}</Typography>
                  <Typography><strong>Duration:</strong> {new Date(selectedRequest.startDate).toLocaleDateString()} - {new Date(selectedRequest.endDate).toLocaleDateString()}</Typography>
                  <Typography><strong>Days:</strong> {selectedRequest.numberOfDays}</Typography>
                  <Typography><strong>Reason:</strong> {selectedRequest.reason}</Typography>
                  <Box><strong>Status:</strong> {renderStatusChip(selectedRequest.status)}</Box>
                </Stack>
              )}
              {detailsTab === 1 && (
                <Box>
                  {selectedRequest.hasAttachment ? (
                    <Button startIcon={<DownloadIcon />} variant="outlined" onClick={() => handleDownloadAttachment(selectedRequest)}>
                      Download {selectedRequest.attachmentFileName || 'Attachment'}
                    </Button>
                  ) : (
                    <Typography color="text.secondary">No attachment uploaded for this leave request.</Typography>
                  )}
                </Box>
              )}
              {detailsTab === 2 && renderProgressTab(selectedRequest)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeLeaveModule;
